import { useState } from 'react';

interface PollCreatorProps {
  onSubmit: (poll: { question: string; options: string[]; allowMultiple: boolean; isAnonymous: boolean }) => void;
  onCancel: () => void;
}

export function PollCreator({ onSubmit, onCancel }: PollCreatorProps) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);

  function addOption() {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  }

  function removeOption(index: number) {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  }

  function updateOption(index: number, value: string) {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  }

  function handleSubmit() {
    const validOptions = options.filter((o) => o.trim().length > 0);
    if (question.trim().length === 0 || validOptions.length < 2) return;
    onSubmit({
      question: question.trim(),
      options: validOptions.map((o) => o.trim()),
      allowMultiple,
      isAnonymous,
    });
  }

  const isValid = question.trim().length > 0 && options.filter((o) => o.trim().length > 0).length >= 2;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-2 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">📊 Create Poll</span>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          ✕
        </button>
      </div>

      <input
        type="text"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Ask a question..."
        maxLength={500}
        className="w-full px-3 py-2 mb-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      <div className="space-y-1.5 mb-2">
        {options.map((option, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <input
              type="text"
              value={option}
              onChange={(e) => updateOption(index, e.target.value)}
              placeholder={`Option ${index + 1}`}
              maxLength={200}
              className="flex-1 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {options.length > 2 && (
              <button
                onClick={() => removeOption(index)}
                className="text-gray-400 hover:text-red-500 text-sm px-1"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {options.length < 10 && (
        <button
          onClick={addOption}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-2"
        >
          + Add option
        </button>
      )}

      <div className="flex items-center gap-4 mb-3 text-sm text-gray-600 dark:text-gray-300">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={allowMultiple}
            onChange={(e) => setAllowMultiple(e.target.checked)}
            className="rounded"
          />
          Multiple choices
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="rounded"
          />
          Anonymous
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isValid}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Poll
        </button>
      </div>
    </div>
  );
}
